/**
 * Три сценария достижения цели.
 *
 * Считается не «что было бы неплохо», а сколько цели реально закрывает
 * каждый набор рычагов в пределах ниши. Если сценарий не дотягивает —
 * это показывается честно, а не подгоняется под 100%.
 */

import { LEVER, LEVER_MAP, estimateScenarioTiming } from './levers';
import { combinationFactorFor, getAvailableCaps } from './ceiling';

const EPS = 1e-9;

const clampPercent = (value) => {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, value));
};

/**
 * Распределяет нужный рост между рычагами равными долями,
 * упирается в предел рычага и перекладывает остаток на свободные.
 *
 * @param {number} requiredMultiplier во сколько раз нужно вырасти
 * @param {Array<{id: string, cap: number}>} leverCaps
 */
export const allocateGrowth = (requiredMultiplier, leverCaps) => {
  const assigned = {};
  leverCaps.forEach((lever) => {
    assigned[lever.id] = 0;
  });

  let remaining = requiredMultiplier;
  let active = leverCaps.filter((lever) => lever.cap > 0);

  while (active.length && remaining > 1 + EPS) {
    const evenGrowth = Math.pow(remaining, 1 / active.length) - 1;
    const capped = active.filter((lever) => lever.cap < evenGrowth - EPS);

    if (!capped.length) {
      for (const lever of active) {
        assigned[lever.id] = evenGrowth;
      }
      remaining = 1;
      break;
    }

    let absorbed = 1;
    for (const lever of capped) {
      assigned[lever.id] = lever.cap;
      absorbed *= 1 + lever.cap;
    }
    remaining /= absorbed;

    const cappedIds = new Set(capped.map((lever) => lever.id));
    active = active.filter((lever) => !cappedIds.has(lever.id));
  }

  const rawMultiplier = leverCaps.reduce((acc, lever) => acc * (1 + assigned[lever.id]), 1);

  return { assigned, rawMultiplier, unmetMultiplier: Math.max(1, remaining) };
};

/**
 * Считает один сценарий.
 */
export const buildScenario = ({ id, name, description, leverIds, state, ceiling, targetRevenue }) => {
  const capsById = ceiling.leverCaps.reduce((acc, lever) => {
    acc[lever.id] = lever.cap;
    return acc;
  }, {});

  const leverCaps = leverIds
    .filter((leverId) => capsById[leverId] !== undefined)
    .map((leverId) => ({ id: leverId, cap: capsById[leverId] }));

  const combinationFactor = combinationFactorFor(leverCaps.length);
  const requiredMultiplier = targetRevenue / state.revenue;

  // Из-за штрафа за совмещение рычаги должны отработать с запасом.
  const grossRequired = Math.max(1, requiredMultiplier / combinationFactor);
  const { assigned, rawMultiplier } = allocateGrowth(grossRequired, leverCaps);

  const achievedMultiplier = rawMultiplier * combinationFactor;
  const achievedRevenue = state.revenue * achievedMultiplier;

  const targetGap = targetRevenue - state.revenue;
  const achievedGain = achievedRevenue - state.revenue;
  const targetCoverage = targetGap > 0 ? clampPercent((achievedGain / targetGap) * 100) : 100;

  const closesTarget = achievedMultiplier >= requiredMultiplier - EPS;
  const maxMultiplier = leverCaps.reduce((acc, lever) => acc * (1 + lever.cap), 1) * combinationFactor;

  return {
    id,
    name,
    description,
    leverIds: leverCaps.map((lever) => lever.id),
    levers: leverCaps.map((lever) => {
      const growth = assigned[lever.id] || 0;
      const currentValue = state.levers[lever.id];

      return {
        id: lever.id,
        label: LEVER_MAP[lever.id].label,
        plainLabel: LEVER_MAP[lever.id].plainLabel,
        action: LEVER_MAP[lever.id].action,
        how: LEVER_MAP[lever.id].how,
        growth,
        growthPercent: growth * 100,
        capPercent: lever.cap * 100,
        atCap: growth >= lever.cap - EPS && growth > 0,
        currentValue,
        targetValue: currentValue * (1 + growth),
      };
    }),
    combinationFactor,
    achievedMultiplier,
    achievedRevenue,
    achievedGain,
    targetCoverage,
    closesTarget,
    maxMultiplier,
    maxRevenue: state.revenue * maxMultiplier,
    timing: estimateScenarioTiming(leverCaps.map((lever) => lever.id)),
  };
};

/**
 * Собирает все три сценария под цель клиента.
 */
export const buildScenarios = ({ state, ceiling, targetRevenue, nicheId }) => {
  const target = Number(targetRevenue) || 0;

  if (!state.valid || target <= state.revenue) {
    return [];
  }

  const available = new Set(state.availableLevers);
  const secondLever = available.has(LEVER.CONVERSION) ? LEVER.CONVERSION : LEVER.LEADS;

  const definitions = [
    {
      id: 'minimal',
      name: 'Минимальный',
      description: 'Один рычаг — тот, что даёт результат быстрее всего',
      leverIds: [LEVER.PRICE],
    },
    {
      id: 'balanced',
      name: 'Сбалансированный',
      description: 'Два рычага: быстрый эффект плюс работа с воронкой',
      leverIds: [LEVER.PRICE, secondLever],
    },
    {
      id: 'full',
      name: 'Полный',
      description: 'Все доступные рычаги одновременно',
      leverIds: getAvailableCaps(nicheId, state.availableLevers).map((lever) => lever.id),
    },
  ];

  return definitions.map((definition) =>
    buildScenario({ ...definition, state, ceiling, targetRevenue: target }),
  );
};

/**
 * Какой сценарий рекомендовать: самый дешёвый из закрывающих цель,
 * а если цель не закрывает никто — тот, что даёт максимум.
 */
export const pickRecommendedScenario = (scenarios) => {
  if (!scenarios.length) return null;

  const closing = scenarios.find((scenario) => scenario.closesTarget);
  if (closing) return closing;

  return scenarios.reduce((best, scenario) =>
    scenario.achievedRevenue > best.achievedRevenue ? scenario : best,
  );
};
